import React, { useEffect, useState } from "react";
import { Drawer, Descriptions, Tag, Typography, Button, Space, Spin, Empty, Timeline, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { assetAPIWithErrorHandling } from "../../../api/asset/asset";

const { Text } = Typography;

/** "Tên (SĐT)" — thiếu phần nào bỏ phần đó, thiếu cả hai thì "—". */
const nguoi = (name, phone) => {
    if (!name && !phone) return "—";
    return phone ? `${name || ""} (${phone})`.trim() : name;
};

/**
 * Một lần đổi khách hàng. Chủ hộ (representative_*) không tự đổi theo khách, nên chỉ
 * hiện dòng chủ hộ khi nó thực sự đổi trong cùng lần sửa — không đổi mà vẫn là người cũ
 * thì người xem thấy ngay ở mục "Chủ hộ" phía trên.
 */
const HistoryItem = ({ h }) => {
    const repChanged =
        (h.from_representative_name || "") !== (h.to_representative_name || "") ||
        (h.from_representative_phone || "") !== (h.to_representative_phone || "");

    return (
        <div>
            <Text type="secondary" className="text-xs">
                {dayjs(h.created_at).format("DD/MM/YYYY HH:mm")}
                {h.changed_by_name ? ` · ${h.changed_by_name}` : ""}
            </Text>
            <div>
                {nguoi(h.from_customer_name, h.from_customer_phone)}
                {" → "}
                <Text strong>{nguoi(h.to_customer_name, h.to_customer_phone)}</Text>
            </div>
            {repChanged && (
                <Text type="secondary" className="text-xs">
                    Chủ hộ: {nguoi(h.from_representative_name, h.from_representative_phone)} →{" "}
                    {nguoi(h.to_representative_name, h.to_representative_phone)}
                </Text>
            )}
        </div>
    );
};

/**
 * Drawer xem chi tiết tài sản.
 *
 * Tài sản chỉ có một khối thông tin ngắn và lịch sử gán khách hàng (vài dòng), không
 * đáng một trang riêng — mở ngay trên danh sách để giữ bộ lọc đang bật.
 *
 * Tự gọi getAssetById thay vì nhận nguyên dòng của bảng, vì drawer này còn được
 * dùng ở màn chi tiết khách hàng nơi bảng tài sản chỉ có vài cột.
 *
 * @param {boolean} open
 * @param {number|string|null} assetId
 * @param {function} onClose
 * @param {function} [onEdit]  Có thì hiện nút "Chỉnh sửa", nhận vào asset vừa tải.
 */
export default function AssetDetailDrawer({ open, assetId, onClose, onEdit }) {
    const [asset, setAsset] = useState(null);
    const [histories, setHistories] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !assetId) return;

        let ignore = false;

        const fetchAsset = async () => {
            setLoading(true);
            try {
                const response = await assetAPIWithErrorHandling.getAssetById(assetId);
                const data = response.data?.data || response.data;
                if (!ignore) setAsset(data);
            } catch (error) {
                if (!ignore) {
                    setAsset(null);
                    message.error(error.message || "Không thể tải thông tin tài sản");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        // Tách riêng: lịch sử lỗi thì vẫn xem được thông tin tài sản.
        const fetchHistories = async () => {
            try {
                const response = await assetAPIWithErrorHandling.getAssignmentHistories(assetId);
                if (!ignore) setHistories(response.data?.data || []);
            } catch (error) {
                if (!ignore) setHistories([]);
            }
        };

        fetchAsset();
        fetchHistories();

        // Đóng rồi mở bản ghi khác nhanh -> bỏ kết quả của lần gọi cũ.
        return () => {
            ignore = true;
        };
    }, [open, assetId]);

    const handleClose = () => {
        setAsset(null);
        setHistories([]);
        onClose?.();
    };

    return (
        <Drawer
            title="Chi tiết tài sản"
            width="min(560px, 100vw)"
            open={open}
            onClose={handleClose}
            destroyOnHidden
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={handleClose}>Đóng</Button>
                    {onEdit && asset && (
                        <Button type="primary" icon={<EditOutlined />} onClick={() => onEdit(asset)}>
                            Chỉnh sửa
                        </Button>
                    )}
                </Space>
            }
        >
            <Spin spinning={loading}>
                {!asset && !loading ? (
                    <Empty description="Không tìm thấy thông tin tài sản" />
                ) : (
                    <>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Mã tài sản">
                                <Text strong>{asset?.code || "-"}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Tên tài sản">{asset?.name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Trạng thái">
                                <Tag color={asset?.status ? "green" : "red"}>
                                    {asset?.status ? "Hoạt động" : "Không hoạt động"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Đơn vị">
                                {asset?.organization?.name ? (
                                    <Tag color="geekblue">{asset.organization.name}</Tag>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Khách hàng sở hữu">
                                {asset?.customer ? (
                                    <div>
                                        <div className="font-medium">{asset.customer.name}</div>
                                        {asset.customer.phone && (
                                            <Text type="secondary" className="text-xs">
                                                {asset.customer.phone}
                                            </Text>
                                        )}
                                    </div>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chi tiết">
                                {asset?.description || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chủ hộ">
                                {asset?.representative_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="SĐT chủ hộ">
                                {asset?.representative_phone || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Tên trên khoản thu">
                                {asset?.use_representative_name ? "Tên chủ hộ" : "Tên khách hàng"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Đường">{asset?.street || "-"}</Descriptions.Item>
                        </Descriptions>

                        <div className="mt-6">
                            <Text strong>Lịch sử gán khách hàng</Text>
                            {histories.length === 0 ? (
                                <div className="mt-2">
                                    <Text type="secondary" className="text-xs">
                                        Chưa đổi khách hàng lần nào
                                    </Text>
                                </div>
                            ) : (
                                <Timeline
                                    className="mt-4"
                                    items={histories.map((h) => ({
                                        key: h.id,
                                        children: <HistoryItem h={h} />,
                                    }))}
                                />
                            )}
                        </div>
                    </>
                )}
            </Spin>
        </Drawer>
    );
}
